import { useRef, useState } from 'react';
import PeriodPicker from './PeriodPicker';
import { PRESETS_ANALYTIC } from '../utils/period';
import FilterButton from './FilterButton';
import FilterSheet from './FilterSheet';
import { useIsMobile } from '../utils/useIsMobile';
import { useStickyFilterOffset } from '../utils/useStickyFilterOffset';
import Icon from './Icon';

// Sticky filter bar for /analysis — same UX as /suppliers and /documents
// (FILTER_AND_DATE_RULES.md). Supplier search + product search on the
// start side, date range anchored to the far end via the picker's own
// .docs-filter-datewrap (margin-inline-start: auto). Extracted verbatim from
// AnalysisPage so it can share the wrapper-component mobile treatment.
//
// ≤720px the bar collapses to supplier search + a "סינון" trigger; product
// search and the date controls move into a FilterSheet (bottom sheet).
export default function AnalysisFilterBar({
  nameFilter,       setNameFilter,
  topProductFilter, setTopProductFilter,
  dateFrom,         dateTo,
  setDateRange,
  // Passed straight through to PeriodPicker. The page computes it, because the
  // page is what owns the figures the caveat is about.
  openPeriod = false,
  hasFilters,
  onClearAll,
  resultCount,
  // Optional mobile sort control (MobileSort node) — rendered beside the
  // סינון trigger; pages that support sorting pass it in.
  sortControl,
  // AnalysisPage only: single-restaurant view has nothing for a supplier/
  // product search to scope (network view's consolidated table is the one
  // page where narrowing to one supplier or product is a real action) — so
  // it hides the search pills, the סינון trigger, and the chip row, leaving
  // only the date range control every mode still needs.
  hideSupplierFilters = false,
}) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetActiveCount = topProductFilter ? 1 : 0;
  const wrapRef = useRef(null);
  useStickyFilterOffset(wrapRef);

  const searchPill = (
    <label className={`docs-filter-control docs-filter-searchpill${nameFilter ? ' is-active' : ''}`}>
      <Icon name="search" size={14} className="docs-filter-microlabel" />
      <input
        className="docs-filter-select"
        value={nameFilter}
        onChange={(e) => setNameFilter(e.target.value)}
        placeholder="חפש לפי שם ספק"
        style={{ minWidth: isMobile ? 0 : 160 }}
      />
    </label>
  );
  const productPill = (
    <label className={`docs-filter-control${topProductFilter ? ' is-active' : ''}`}>
      <Icon name="tag" size={14} className="docs-filter-microlabel" />
      <input
        className="docs-filter-select"
        value={topProductFilter}
        onChange={(e) => setTopProductFilter(e.target.value)}
        placeholder="חפש לפי שם מוצר"
        style={{ minWidth: isMobile ? 0 : 160 }}
      />
    </label>
  );
  // One date control for both months and arbitrary day ranges. `compact` is the
  // mobile-row variant — same component and state, just no microlabel.
  const dateRange = (variant) => (
    <PeriodPicker
      dateFrom={dateFrom}
      dateTo={dateTo}
      onChange={(f, t) => setDateRange(f, t)}
      presets={PRESETS_ANALYTIC}
      variant={variant}
      openPeriod={openPeriod}
    />
  );

  // Whether the chips row has anything to show. NOT `hasFilters`: that is also
  // true for the default month range, which renders no chip of its own (the
  // period is already a pill in the bar) — so the row would come up empty apart
  // from a lone "נקה הכל" with nothing to clear. Shared by both layouts so the
  // two can't drift; mobile used to carry this fix alone.
  const hasChips = Boolean(nameFilter || topProductFilter);

  const renderChips = () => (
    <div className="docs-filter-chips">
      {nameFilter && (
        <span className="docs-filter-chip">
          חיפוש: {nameFilter}
          <button
            type="button"
            aria-label="נקה חיפוש"
            onClick={() => setNameFilter('')}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      {topProductFilter && (
        <span className="docs-filter-chip">
          מוצר: {topProductFilter}
          <button
            type="button"
            aria-label="נקה חיפוש מוצר"
            onClick={() => setTopProductFilter('')}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      <button type="button" className="docs-filter-clearall" onClick={onClearAll}>
        נקה הכל
      </button>
    </div>
  );

  if (isMobile) {
    if (hideSupplierFilters) {
      return <div className="period-bar" ref={wrapRef}>{dateRange('bar')}</div>;
    }
    return (
      <>
      <div className="period-bar" ref={wrapRef}>{dateRange('bar')}</div>
      <div className="docs-filter-wrap">
        <div className="docs-filter-bar docs-filter-bar--mobile">
          {searchPill}
          <FilterButton
            activeCount={sheetActiveCount}
            expanded={sheetOpen}
            onClick={() => setSheetOpen(true)}
          />
          {sortControl}
        </div>
        {hasChips && renderChips()}

        <FilterSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          resultCount={resultCount}
          onClearAll={onClearAll}
        >
          {productPill}
        </FilterSheet>
      </div>
      </>
    );
  }

  if (hideSupplierFilters) {
    return (
      <div className="docs-filter-wrap" ref={wrapRef}>
        <div className="docs-filter-bar">{dateRange('pill')}</div>
      </div>
    );
  }

  return (
    <div className="docs-filter-wrap" ref={wrapRef}>
      <div className="docs-filter-bar">
        {searchPill}
        {productPill}
        {dateRange('pill')}
      </div>
      {hasChips && renderChips()}
    </div>
  );
}
